import { FC, useMemo, useState } from 'react';
import { upperFirst } from 'lodash';
import { Subscription } from '@demind-inc/core';
import { LoadingButton } from '@mui/lab';

import './SubscriptionSettings.scss';
import {
  SKIP_CARD_PRODUCT_ID,
  useAuthContext,
  useCancelStripeSubscription,
  useSubscriptionContext,
} from '../../../../data-access';
import { UnsubscribeDialog } from './UnsubscribeDialog';
import {
  checkFreeTrial,
  getFormattedSubscriptionDates,
  getFormattedPrice,
  getPriceAfterTrial,
  getPriceWithRecurring,
  getLifetimePlanEmailLink,
} from './helpers';
import { ExpiredSubscriptionTimeline, SubscriptionTimeline } from '../../../Subscriptions';
import { CustomTooltip } from '../../../common';
import { Button } from '@mui/material';
import AccountActions from './AccountActions';
import { useTranslation } from 'react-i18next';

interface SubscriptionSettingsProps {
  showAccountActions?: boolean;
}

const SubscriptionSettings: FC<SubscriptionSettingsProps> = ({ showAccountActions = true }) => {
  const { t } = useTranslation();
  const [unsubscribeDialogVisible, setUnsubscribeDialogVisible] = useState(false);
  const { currentSubscription, lifetimePlanPrice } = useSubscriptionContext();
  const {
    cancelStripeSubscription,
    error: cancelError,
    isPending: isCancelling,
  } = useCancelStripeSubscription();
  const { user } = useAuthContext();

  const { isFreePlanOrGrant, isInPostPaywallTrial } = useMemo(() => {
    const isFreePlanOrGrant =
      user.access === 'free' ||
      (currentSubscription?.status === 'freeTrial' &&
        currentSubscription?.productId === SKIP_CARD_PRODUCT_ID);

    const isInPostPaywallTrial =
      currentSubscription?.status === 'freeTrial' &&
      currentSubscription?.productId === SKIP_CARD_PRODUCT_ID;

    return { isFreePlanOrGrant, isInPostPaywallTrial };
  }, [user, currentSubscription]);

  const subscription = currentSubscription as Subscription;
  const isLifetimePlan = subscription?.recurring === 'LIFETIME';
  const isInFreeTrial = subscription ? checkFreeTrial(subscription) : false;
  const formattedDates = subscription ? getFormattedSubscriptionDates(subscription) : undefined;
  const price = subscription ? getFormattedPrice(subscription) : '';
  const priceAfterTrial = subscription
    ? getPriceAfterTrial(subscription, price, isFreePlanOrGrant)
    : '';
  const priceWithRecurring = subscription ? getPriceWithRecurring(subscription, price) : '';
  const { priceLabel: lifetimePriceLabel, mailto: lifetimeMailto } = getLifetimePlanEmailLink(
    lifetimePlanPrice,
    user.email
  );
  const shouldShowLifetimeButton = !isLifetimePlan && !isFreePlanOrGrant;

  const handleUnsubscribe = () => {
    cancelStripeSubscription({ userId: user.userId });
  };

  const renderTimeline = () => {
    if (isInFreeTrial && !formattedDates?.unsubscribedDate) {
      return (
        <SubscriptionTimeline
          startTrialDate={formattedDates?.startFreeTrialDate}
          startChargeDate={formattedDates?.startChargeDate}
          price={priceAfterTrial}
          className="subscription-settings__timeline"
        />
      );
    }

    if (isInFreeTrial && formattedDates?.unsubscribedDate) {
      // If user has unsubscribed but extened the trial
      return (
        <ExpiredSubscriptionTimeline
          unsubscribedDate={formattedDates.unsubscribedDate}
          expiredDate={formattedDates?.startChargeDate!}
          className="subscription-settings__timeline"
        />
      );
    }

    if (
      subscription?.status === 'unsubscribed' &&
      formattedDates.unsubscribedDate &&
      formattedDates.expirationDate
    ) {
      return (
        <ExpiredSubscriptionTimeline
          unsubscribedDate={formattedDates.unsubscribedDate}
          expiredDate={formattedDates?.expirationDate!}
          className="subscription-settings__timeline"
        />
      );
    }

    return <div className="subscription-settings__free-trial-container">{priceWithRecurring}</div>;
  };

  const shouldShowCancelButton =
    !isFreePlanOrGrant &&
    !isLifetimePlan &&
    !isInPostPaywallTrial &&
    ['freeTrial', 'active'].includes(subscription?.status!) &&
    !subscription?.unsubscribedDate; // If user has unsubscribed, do not show the cancel button. in case the trial is extended.

  const isMobileSubscription = subscription?.deviceFrom === 'mobile';

  return (
    <div className="subscription-settings">
      <div className="subscription-settings__plan">
        <div className="subscription-settings__plan__title">
          {isInPostPaywallTrial
            ? t('settings.subscription.trial')
            : isFreePlanOrGrant
              ? t('settings.subscription.free')
              : t(`settings.subscription.recurring.${subscription?.recurring?.toLowerCase()}`, {
                  defaultValue: upperFirst(subscription?.recurring?.toLowerCase()),
                })}{' '}
          {t('settings.subscription.plan')}{' '}
          {subscription?.status === 'unsubscribed' && (
            <p className="subscription-settings__plan__title--small">
              {t('settings.subscription.unsubscribed')}
            </p>
          )}
          {renderTimeline()}
        </div>
      </div>

      {!!cancelError && <div className="subscription-settings__error">*{cancelError?.message}</div>}

      <div className="subscription-settings__buttons-group">
        {shouldShowCancelButton && (
          <CustomTooltip
            title={isMobileSubscription ? t('settings.subscription.cancelFromMobile') : ''}
            disableInteractive={!isMobileSubscription}
            disableHoverListener={!isMobileSubscription}
            disableFocusListener={!isMobileSubscription}
            disableTouchListener={!isMobileSubscription}
          >
            <span>
              <LoadingButton
                variant="outlined"
                className="subscription-settings__unsubscribe"
                onClick={() => setUnsubscribeDialogVisible(true)}
                loading={isCancelling}
                disabled={isCancelling || isMobileSubscription}
              >
                {t('settings.subscription.cancelSubscription')}
              </LoadingButton>
            </span>
          </CustomTooltip>
        )}

        {shouldShowLifetimeButton && !!lifetimePriceLabel && (
          <Button
            className="subscription-settings__plan__lifetime-button"
            component="a"
            type="text"
            href={lifetimeMailto}
            variant="outlined"
          >
            {t('settings.subscription.changeToLifetimePlan', { price: lifetimePriceLabel })}
          </Button>
        )}
      </div>

      <UnsubscribeDialog
        open={unsubscribeDialogVisible}
        onClose={() => setUnsubscribeDialogVisible(false)}
        onUnsubscribe={handleUnsubscribe}
      />

      {showAccountActions && <AccountActions />}
    </div>
  );
};

export default SubscriptionSettings;
